import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogComponent } from '../component/dialog/confirmation-dialog/confirmation-dialog.component';
import { ControlMessageService } from './control-message.service';
import { ControlMessageType } from '../interface/enum-controlMessages-type';
import { ControlMessage } from '../interface/control-message';
import { LayoutService } from './layout.service';
import { first, firstValueFrom } from 'rxjs';
import { MkgOS } from '../class/mkg-os';
import { ChoosePhoneToMessageComponent } from '../component/dialog/choose-phone-to-message/choose-phone-to-message.component';
import { Utilities } from '../class/utilities';
import { ClientUtilities } from '../class/client-utilities';
import { ClientService } from './client.service';

@Injectable({
  providedIn: 'root'
})
export class WhatsappService {

  constructor(
    private dataService: DataService,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private controlMessageService: ControlMessageService,
    private _layout: LayoutService,
    private clientService: ClientService
  ) { }

  // Carrega todas as mensagens e retorna a mensagem correspondente ao id fornecido
  async carregaMensagem(id: number): Promise<ControlMessage | undefined> {
    await this.controlMessageService.loadMessages();
    return this.controlMessageService.getMessageByTypeId(id);
  }

  // Retorna o typeId correspondente ao rótulo fornecido
  private getMessageTypeLabel(label: string): number {
    switch (label) {
      case 'ABRIR_ORÇAMENTO':
        return ControlMessageType.AbrirOrcamento;
      case 'ABRIR_OS':
        return ControlMessageType.AbrirOS;
      case 'FECHAR_OS':
        return ControlMessageType.FecharOS;
      case 'SOLICITAR_AUTORIZACAO':
        return ControlMessageType.SolicitacaoAtualizacao;
      case 'POS_VENDA':
        return ControlMessageType.PosVenda;
      case 'ENVIAR_PESQUISA':
        return ControlMessageType.PesquisaDeSatisfacao;
        case 'ANIVERSARIANTE':
        return ControlMessageType.Aniversariante;
      default:
        return -1; // Valor inválido
    }
  }

  // Substitui placeholders na mensagem com valores fornecidos
  private replacePlaceholders(message: string, replacements: { [key: string]: string }): string {
    return message.replace(/{{\s*([^{}\s]+)\s*}}/g, (match, key) => replacements[key] || match);
  }

  async openDialog(label: string, ro: MkgOS): Promise<boolean> {
    this._layout.addLoaderTrigger('WhatsappService.openDialog()');

    try {
      if (!ro || !ro.client || (!ro.telefTmp && ClientUtilities.isDefault(ro.client?.document))) {
          this._layout.removeLoaderTrigger('WhatsappService.openDialog()');
          return false;
        }
        const isDefaultClientWithPhoneTemp = (!!ro.telefTmp && ClientUtilities.isDefault(ro.client?.document));

      let phone: string = isDefaultClientWithPhoneTemp ? Utilities.removeMask(ro.telefTmp) : undefined;

      if (!isDefaultClientWithPhoneTemp) {
        if (ro.client.phone1?.trim() && ro.client.phone2?.trim()) {
           if(ro.client.whatsapp && ro.client.whatsapp.trim() !== ''){
            phone = ro.client.whatsapp;
          }else{
            this._layout.removeLoaderTrigger('WhatsappService.openDialog()');
            phone = await firstValueFrom(this.dialog.open(ChoosePhoneToMessageComponent, {
              data: [ro.client.phone1.trim(), ro.client.phone2.trim()],
              disableClose: true
            }).afterClosed());
            this._layout.addLoaderTrigger('WhatsappService.openDialog()');
            if (!phone) {
              this._layout.removeLoaderTrigger('WhatsappService.openDialog()');
              return false;
            }

            const clientFull = await this.clientService.get(ro.client.id)
            clientFull.whatsapp = phone;
            await this.clientService.update(clientFull)
          }
        } else {
          phone = ro.client.phone1;
        }
      }

      const name = this.dataService.company.fancyName.length ? this.dataService.company.fancyName : this.dataService.company.socialName;

      if (!name || (!phone || (phone && (Utilities.numberFieldsLengthIsInvalid(phone, 10, true) && Utilities.numberFieldsLengthIsInvalid(phone, 11, true))))) {
        this._layout.removeLoaderTrigger('WhatsappService.openDialog()');
        return false;
      }

      // Tenta obter o typeId da mensagem configurada pelo usuário
      const type = this.getMessageTypeLabel(label);

      let message: string;

    // Se a mensagem não for encontrada, utiliza a mensagem padrão correspondente ao label
    const defaultMessages: { [key: string]: string } = {
      'ABRIR_ORÇAMENTO': 'TRACK_RO',
      'SOLICITAR_AUTORIZACAO': 'RO_READY',
      'ABRIR_OS': 'RO_OPENED',
      'FECHAR_OS': 'WHATS_FECHAR_OS',
      'RO_REJECTED': 'RO_REJECTED',
      'ACOMPANHAMENTO_OS': 'TRACK_RO',
      'undefined': 'TRACK_RO',
      'RO_UPDATED': 'RO_UPDATED',
    };

      if (type !== -1) {
        const mensagem = await this.carregaMensagem(type);
        if (mensagem) {
          message = this.replacePlaceholders(mensagem.message,
            {
            company: name,
            plate: ro.vehicleTmp ?? ro.vehicle.plate,
            client: ro.clientTmp ?? ro.client.name,
            budget: ro?.budgetCode.toString(),
            os: ro?.codeSystem.toString()
          });
        } else {
          message = this.translateService.instant(`WHATSAPP.${defaultMessages[label]}`, { company: name, plate: ro.vehicleTmp ?? ro.vehicle.plate });
        }
      } else {

        if (defaultMessages[label]) {
          // Se o typeId não for válido, utiliza a mensagem padrão geral
          message = this.translateService.instant(`WHATSAPP.${defaultMessages[label]}`, { company: name, plate: ro.vehicleTmp ?? ro.vehicle.plate });
        } else {
          // Se não houver mensagem padrão correspondente, utiliza o texto recebido
          message = label;
        }
      }

      const defaultMessage = this.translateService.instant('WHATSAPP.ADD_OUR_CONTACT');
      const link = `https://wa.me/55${phone}?text=${message} ${defaultMessage} ${ro.shortUrl}`;

      this._layout.removeLoaderTrigger('WhatsappService.openDialog()');
      const result = await firstValueFrom(this.dialog
        .open(ConfirmationDialogComponent, {
          data: {
            text: 'SEND_LINK',
            link: link,
            // svg: whatsAppIcon
          }
        })
        .afterClosed());

      return result;
    } catch (error) {
      console.error('Error in WhatsApp dialog:', error);
      throw error;
    } finally {
      this._layout.removeLoaderTrigger('WhatsappService.openDialog()');
    }
  }

  async openWhatsCs(message: string): Promise<boolean> {
    const csPhone = "554599920045";
    const formatMessage = encodeURIComponent(message);

    const link = `https://wa.me/${csPhone}?text=${formatMessage}`;
    window.open(link, '_blank');

    return true;
  }
}
